import { useState } from "react";
import { useIsMobile } from "../lib/useIsMobile";
import { NAVY } from "../styles/palette";
import { getAuthToken, SUPABASE_ANON_KEY, SUPABASE_URL } from "../lib/supabase";
import { DAY_COLORS } from "../lib/serviceArea";

const DAYS_OF_WEEK = ["monday","tuesday","wednesday","thursday","friday","saturday","sunday"];

export function ServiceAreaEditor({ serviceArea, zipSecondDay = {}, onRefresh, showToast }) {
  const isMobile = useIsMobile();
  const [newZip,    setNewZip]    = useState("");
  const [newDay,    setNewDay]    = useState("saturday");
  const [saving,    setSaving]    = useState(false);

  // Build display from serviceArea prop
  const allZips = Object.entries(serviceArea).flatMap(([day, zips]) =>
    zips.map(zip => ({ zip, day }))
  ).sort((a,b) => a.zip.localeCompare(b.zip));

  // Re-route EXISTING clients in a zip the moment its service day changes (add /
  // move / remove) — not just future signups. For each active client in the zip:
  // drop their future, still-scheduled AUTO visits (never manual, completed, or
  // past), then rebuild this month + next from today off the just-saved mapping.
  // generate-monthly-jobs reads service_area fresh and skips a now-unmapped zip,
  // so a removal simply clears the upcoming visits.
  async function rescheduleZip(zip) {
    const H = { apikey: SUPABASE_ANON_KEY, Authorization: `Bearer ${getAuthToken()}` };
    const today = new Date().toISOString().slice(0, 10);
    const now = new Date();
    let affected = [];
    try {
      affected = await fetch(`${SUPABASE_URL}/rest/v1/clients?zip=eq.${zip}&active=eq.true&select=id`, { headers: H }).then(r => r.json());
    } catch { affected = []; }
    if (!Array.isArray(affected) || affected.length === 0) return 0;
    for (const c of affected) {
      await fetch(`${SUPABASE_URL}/rest/v1/jobs?client_id=eq.${c.id}&scheduled_date=gte.${today}&status=eq.scheduled&source=eq.auto`, { method: "DELETE", headers: H }).catch(() => {});
      for (const m of [0, 1]) {
        const d = new Date(now.getFullYear(), now.getMonth() + m, 1);
        await fetch(`${SUPABASE_URL}/functions/v1/generate-monthly-jobs`, {
          method: "POST", headers: { ...H, "Content-Type": "application/json" },
          body: JSON.stringify({ client_id: c.id, year: d.getFullYear(), month: d.getMonth(), from_date: today }),
        }).catch(() => {});
      }
    }
    return affected.length;
  }

  async function addZip() {
    const zip = newZip.trim();
    if (!zip) return;
    // Check not already assigned
    for (const [day, zips] of Object.entries(serviceArea)) {
      if (zips.includes(zip)) {
        showToast(`${zip} is already assigned to ${day}`, "error");
        return;
      }
    }
    setSaving(true);
    try {
      await fetch(`${SUPABASE_URL}/rest/v1/service_area`,{method:"POST",headers:{apikey:SUPABASE_ANON_KEY,Authorization:`Bearer ${getAuthToken()}`,"Content-Type":"application/json",Prefer:"return=minimal"},body:JSON.stringify({ zip, day: newDay })});
      setNewZip("");
      await onRefresh();
      const n = await rescheduleZip(zip);
      showToast(`${zip} added to ${newDay}${n ? ` · re-routed ${n} client${n>1?"s":""}` : ""}`);
    } catch(e) { showToast("Error adding zip","error"); }
    setSaving(false);
  }

  async function removeZip(zip, day) {
    if (!window.confirm(`Remove zip ${zip} from ${day}? Clients with this zip will show as "Out of Area".`)) return;
    await fetch(`${SUPABASE_URL}/rest/v1/service_area?zip=eq.${zip}`, {
      method: "DELETE",
      headers: { apikey: SUPABASE_ANON_KEY, Authorization: `Bearer ${getAuthToken()}` },
    });
    await onRefresh();
    const n = await rescheduleZip(zip);
    showToast(`${zip} removed${n ? ` · cleared upcoming visits for ${n} client${n>1?"s":""}` : ""}`);
  }

  async function changeDay(zip, oldDay, newDayVal) {
    // A "second" day equal to the primary is rejected by the DB (it would double-book the
    // same date), so moving the primary ONTO the current second day clears the second.
    const patch = { day: newDayVal };
    if (zipSecondDay[zip] === newDayVal) patch.second_day = null;
    await fetch(`${SUPABASE_URL}/rest/v1/service_area?zip=eq.${zip}`, {
      method: "PATCH",
      headers: { apikey: SUPABASE_ANON_KEY, Authorization: `Bearer ${getAuthToken()}`, "Content-Type": "application/json", Prefer: "return=minimal" },
      body: JSON.stringify(patch),
    });
    await onRefresh();
    const n = await rescheduleZip(zip);
    showToast(`${zip} moved to ${newDayVal}${n ? ` · re-routed ${n} client${n>1?"s":""}` : ""}`);
  }

  // The OPTIONAL second service day. Only twice-weekly clients use it — weekly and
  // biweekly clients in the zip are unaffected. Without one, a twice-weekly client is
  // scheduled on the primary day alone while still billed the twice-weekly rate.
  async function changeSecondDay(zip, val) {
    await fetch(`${SUPABASE_URL}/rest/v1/service_area?zip=eq.${zip}`, {
      method: "PATCH",
      headers: { apikey: SUPABASE_ANON_KEY, Authorization: `Bearer ${getAuthToken()}`, "Content-Type": "application/json", Prefer: "return=minimal" },
      body: JSON.stringify({ second_day: val || null }),
    });
    await onRefresh();
    const n = await rescheduleZip(zip);
    showToast(val
      ? `${zip} twice-weekly clients now also served ${val}${n ? ` · re-routed ${n} client${n>1?"s":""}` : ""}`
      : `${zip} second day cleared${n ? ` · re-routed ${n} client${n>1?"s":""}` : ""}`);
  }

  return (
    <div className="card" style={{gridColumn:"1/-1"}}>
      <div className="card-title">📍 Service Area & Zip Codes</div>
      <div style={{fontSize:12,color:"var(--text2)",marginBottom:16,lineHeight:1.6}}>
        Manage which zip codes you service and which day they're assigned to. Clients with zips not listed here will be flagged as "Out of Area."
        {" "}The <strong>+ 2nd</strong> dropdown is optional — it's the second visit day for <em>twice-weekly</em> clients in that zip only. Weekly and biweekly clients are never affected by it.
      </div>

      {/* Add zip form */}
      <div style={{display:"flex",gap:10,marginBottom:20,flexWrap:"wrap"}}>
        <input
          value={newZip}
          onChange={e=>setNewZip(e.target.value.replace(/\D/g,"").slice(0,5))}
          placeholder="Enter zip code"
          maxLength={5}
          inputMode="numeric"
          style={isMobile?{flex:"1 1 120px",minWidth:0}:{width:130}}
          onKeyDown={e=>e.key==="Enter"&&addZip()}
        />
        <select value={newDay} onChange={e=>setNewDay(e.target.value)} style={isMobile?{flex:"1 1 130px",minWidth:0}:{width:140}}>
          {DAYS_OF_WEEK.map(d=><option key={d} value={d}>{d.charAt(0).toUpperCase()+d.slice(1)}</option>)}
        </select>
        <button className="btn btn-gold" onClick={addZip} disabled={saving||newZip.length<5}>
          {saving?"Adding...":"+ Add Zip"}
        </button>
      </div>

      {/* Zips by day */}
      {allZips.length === 0 ? (
        <div className="empty"><p>No zip codes added yet. Add your first service area zip above.</p></div>
      ) : (
        <div>
          {DAYS_OF_WEEK.map(day => {
            const dayZips = allZips.filter(z => z.day === day);
            if (dayZips.length === 0) return null;
            return (
              <div key={day} style={{marginBottom:16}}>
                <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:8}}>
                  <div style={{width:12,height:12,borderRadius:"50%",background:DAY_COLORS[day]||"var(--text2)",flexShrink:0}}/>
                  <span style={{fontWeight:700,fontSize:13,color:"var(--text)",textTransform:"capitalize"}}>{day}</span>
                  <span style={{fontSize:11,color:"var(--text2)"}}>({dayZips.length} zip{dayZips.length!==1?"s":""})</span>
                </div>
                <div style={{display:"flex",flexWrap:"wrap",gap:6,paddingLeft:20}}>
                  {dayZips.map(({zip}) => {
                    const second = zipSecondDay[zip] || "";
                    return (
                    <div key={zip} style={{display:"flex",alignItems:"center",gap:4,background:"#f0f4ff",border:"1px solid #c7d2fe",borderRadius:20,padding:isMobile?"2px 2px 2px 12px":"3px 4px 3px 10px",maxWidth:"100%"}}>
                      <span style={{fontSize:isMobile?13:12,fontWeight:500,color:NAVY}}>{zip}</span>
                      <select
                        value={day}
                        aria-label={`Service day for ${zip}`}
                        onChange={e=>changeDay(zip, day, e.target.value)}
                        style={{fontSize:10,border:"none",background:"transparent",color:"var(--text2)",cursor:"pointer",padding:"0 2px",minHeight:isMobile?36:undefined}}
                      >
                        {DAYS_OF_WEEK.map(d=><option key={d} value={d}>{d.slice(0,3)}</option>)}
                      </select>
                      <span style={{fontSize:10,color:"var(--text2)",opacity:.6}}>+</span>
                      <select
                        value={second}
                        title="Second service day — twice-weekly clients in this zip are also served on this day"
                        aria-label={`Second service day for ${zip} (twice-weekly clients only)`}
                        onChange={e=>changeSecondDay(zip, e.target.value)}
                        style={{fontSize:10,border:"none",background:"transparent",cursor:"pointer",padding:"0 2px",fontWeight:second?600:400,color:second?NAVY:"var(--text2)",minHeight:isMobile?36:undefined}}
                      >
                        <option value="">2nd…</option>
                        {DAYS_OF_WEEK.filter(d=>d!==day).map(d=><option key={d} value={d}>{d.slice(0,3)}</option>)}
                      </select>
                      {/* Phones: a 16px × is not a tap target — give it a 36px hit area. */}
                      <button
                        onClick={()=>removeZip(zip, day)}
                        aria-label={`Remove ${zip}`}
                        style={isMobile
                          ? {background:"none",border:"none",cursor:"pointer",color:"#bf2600",fontSize:18,padding:0,lineHeight:1,minWidth:36,minHeight:36,display:"inline-flex",alignItems:"center",justifyContent:"center",borderRadius:18}
                          : {background:"none",border:"none",cursor:"pointer",color:"#bf2600",fontSize:14,padding:"0 4px",lineHeight:1}}
                      >×</button>
                    </div>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>
      )}

      <div style={{marginTop:16,fontSize:11,color:"var(--text2)",background:"var(--bg)",borderRadius:6,padding:"8px 12px"}}>
        💡 Changes take effect immediately — existing clients in a zip are automatically re-routed to the new day (upcoming visits only; completed and past visits are never touched).
      </div>
    </div>
  );
}
